import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCalendarPlus, faCalendar, faEye, faEyeSlash, faRotate } from "@fortawesome/pro-regular-svg-icons";
import { useHistory } from "react-router-dom";
import { openModal } from "../../store/modal/modalReducer";

import { toast } from "react-toastify";

import { format, isValid, parse } from "date-fns";

export default function GridBookingAppointments({ setDoc, db }) {
    const dispatch = useDispatch();
    const [appointmentList, setAppointmentList] = useState([]);
    const { current_booking } = useSelector((state) => state.booking);
    const [clickOnce, setClickOnce] = useState(true);
    const [showCancelled, setShowCancelled] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(async () => {
        if (clickOnce === true) {
            setClickOnce(false);
            setLoading(true);

            axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}appointment_list`,
                data: { booking_reference: current_booking.booking_reference },
            })
                .then((response) => {
                    console.log('Appointment list data:', response.data);
                    setAppointmentList(response.data);
                })
                .catch(() => {
                    setAppointmentList([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, []);

    const refreshAppointments = () => {
        setLoading(true);
        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}appointment_list`,
            data: { booking_reference: current_booking.booking_reference },
        })
            .then((response) => {
                setAppointmentList(response.data);
            })
            .catch(() => {
                setAppointmentList([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Calculate cancelled appointments count
    const cancelledCount = appointmentList?.filter(apt => apt.status === 4).length || 0;
    
    // Filter appointments based on showCancelled state
    const filteredAppointments = showCancelled 
        ? appointmentList 
        : appointmentList?.filter(apt => apt.status !== 4);

    return (
        <div className="overflow-y-scroll scroll-smooth h-96">
            <div className="flex items-center justify-between mb-2">
                {cancelledCount > 0 && (
                    <button
                        onClick={() => setShowCancelled(!showCancelled)}
                        className={`px-3 h-10 rounded transition-colors flex items-center space-x-2 ${
                            showCancelled 
                                ? 'bg-gray-600 text-white hover:bg-gray-700' 
                                : 'bg-gray-400 text-white hover:bg-gray-500'
                        }`}
                        title={showCancelled ? "Hide cancelled appointments" : "Show cancelled appointments"}
                    >
                        <FontAwesomeIcon icon={showCancelled ? faEyeSlash : faEye} className="h-4 w-4" />
                        <span className="text-xs font-medium whitespace-nowrap">
                            {showCancelled ? "Hide Cancelled" : `Cancelled (${cancelledCount})`}
                        </span>
                    </button>
                )}
                {cancelledCount === 0 && <div></div>}
                <div className="flex items-center space-x-2">
                    <button
                        onClick={refreshAppointments}
                        className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        title="Refresh Appointments"
                    >
                        <FontAwesomeIcon icon={faRotate} className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => {
                            const propertyId = current_booking?.property_id;
                            const calendarUrl = propertyId 
                                ? `/calendar/landing?property=${propertyId}`
                                : '/calendar/landing';
                            window.open(calendarUrl, '_blank');
                        }}
                        className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        title="Open Calendar"
                    >
                        <FontAwesomeIcon icon={faCalendar} className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() =>
                            dispatch(
                                openModal({
                                    modal_type: "ModalManualAppointment",
                                    modal_props: {
                                        onSuccess: refreshAppointments,
                                    },
                                })
                            )
                        }
                        className="p-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                        title="Load Appointment"
                    >
                        <FontAwesomeIcon icon={faCalendarPlus} className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500 text-lg">Loading...</p>
                </div>
            ) : filteredAppointments?.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500">No appointments found</p>
                </div>
            ) : (
                filteredAppointments?.map((appointment) => (
                <div
                    className="border-2 w-full rounded px-1 py-1 my-2 cursor-pointer hover:-translate-y-0.5 hover:bg-dark_background hover:text-white"
                    onClick={(a) =>
                        dispatch(
                            openModal({
                                modal_type: "ModalViewAppointment",
                                modal_props: {
                                    booking_id: appointment.id,
                                },
                            })
                        )
                    }
                >
                    <p>
                        {appointment?.appointment?.name || appointment?.appointment_url || "Unnamed Appointment"} -{" "}
                        <span className={appointment?.status === 4 ? "text-red-600 font-semibold" : ""}>
                            {appointment?.status === 1 ? "Requested" : appointment?.status === 2 ? "Confirmed" : appointment?.status === 3 ? "Completed" : appointment?.status === 4 ? "Cancelled" : "Unknown"}
                        </span>
                    </p>
                    <p>
                        {(() => {
                            const propName = appointment?.appointment?.property?.name || "";

                            let aos_name = "";
                            if (appointment?.booking_aos?.unit?.name) {
                                aos_name = ", Unit " + appointment?.booking_aos?.unit?.name;
                            }

                            if (propName.startsWith("Unison -")) {
                                return "Unison" + aos_name;
                            }

                            if (propName) {
                                return propName + aos_name;
                            }
                            return "Unknown Property" + aos_name;
                        })()}
                    </p>
                    <p>{isValid(new Date(appointment?.booked_date)) ? format(new Date(appointment?.booked_date), "EEE, dd MMMM yyyy", { timeZone: "UTC" }) : "Invalid date"}</p>
                    <p>
                        {appointment?.booked_time}
                        {appointment?.booked_time && appointment?.time_to && " - "}
                        {appointment?.time_to}
                    </p>
                    {appointment?.no_show_status && (
                        <p className="font-semibold">Status: {appointment.no_show_status}</p>
                    )}
                    {appointment?.tenant_satisfaction && (
                        <p>
                            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                appointment.tenant_satisfaction.toLowerCase().includes('satisfied') && !appointment.tenant_satisfaction.toLowerCase().includes('not')
                                    ? 'bg-green-100 text-green-800'
                                    : appointment.tenant_satisfaction.toLowerCase().includes('not')
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                            }`}>
                                {appointment.tenant_satisfaction}
                            </span>
                        </p>
                    )}
                    {(appointment?.assigned_user || appointment?.assignedUser) && (
                        <p>Assigned: {(appointment.assigned_user || appointment.assignedUser).name} {(appointment.assigned_user || appointment.assignedUser).surname}</p>
                    )}
                    {appointment?.ticket?.asana_project_gid && (
                        <p className="text-sm text-gray-600">Asana Project: {appointment.ticket.asana_project_gid}</p>
                    )}
                    {appointment?.ticket?.asana_assignee_gid && (
                        <p className="text-sm text-gray-600">Asana Assignee: {appointment.ticket.asana_assignee_gid}</p>
                    )}
                    {appointment?.ticket?.assistance_type && (
                        <p className="text-sm text-gray-600">Type: {appointment.ticket.assistance_type}</p>
                    )}
                    {appointment?.ticket?.maintenance_option && (
                        <p className="text-sm text-gray-600">Option: {appointment.ticket.maintenance_option}</p>
                    )}
                    {appointment?.ticket?.maintenance_description && (
                        <p className="text-sm text-gray-600">Details: {appointment.ticket.maintenance_description}</p>
                    )}
                    <p>{appointment?.ticket?.external_task_id && "ASANA TICKET"}</p>
                </div>
            )))}
        </div>
    );
}
